// src/pages/PrintableDocuments.js
import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Stack,
  Divider,
  Checkbox,
  Button,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { PDFDocument } from "pdf-lib";

const mandatoryDocuments = [
  {
    title: "Certificate of Registry",
    description:
      "Certificate of the ship's registry. Needed to gain access to port.",
    file: "/pdf/certificateofregistry.pdf",
  },
  {
    title: "International Oil Pollution Prevention Certificate (IOPP)",
    description: "Needed according to MARPOL Annex I regulations.",
    file: "/pdf/IOPP.pdf",
  },
];

const otherDocuments = [
  {
    title: "Notice of arrival",
    description:
      "Document for quay to sign that the ship has arrived according to plan.",
    file: "/pdf/noticeofarrival.pdf",
  },
];

export default function PrintableDocuments() {
  const [selectedDocs, setSelectedDocs] = useState([]);

  const toggleSelection = (file) => {
    setSelectedDocs((prev) =>
      prev.includes(file) ? prev.filter((f) => f !== file) : [...prev, file]
    );
  };

  const handlePrint = (file) => {
    const win = window.open(file, "_blank");
    if (win) {
      win.focus();
      win.print();
    }
  };

  const handlePrintSelected = async () => {
    if (selectedDocs.length === 0) return;

    const mergedPdf = await PDFDocument.create();

    for (const file of selectedDocs) {
      const response = await fetch(file);
      const arrayBuffer = await response.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);

      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      copiedPages.forEach((page) => mergedPdf.addPage(page));
    }

    const mergedPdfBytes = await mergedPdf.save();
    const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);

    const win = window.open(url, "_blank");
    if (win) {
      win.focus();
      win.onload = () => {
        win.print();
      };
    }
  };

  const renderList = (title, docs) => (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Stack spacing={2}>
        {docs.map((doc, index) => (
          <Card
            key={index}
            variant="outlined"
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Checkbox
              checked={selectedDocs.includes(doc.file)}
              onChange={() => toggleSelection(doc.file)}
              sx={{ mr: 2 }}
            />

            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h6">{doc.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {doc.description}
              </Typography>
            </CardContent>

            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

            <IconButton
              color="primary"
              onClick={() => handlePrint(doc.file)}
              sx={{ mr: 1 }}
            >
              <PrintIcon />
            </IconButton>
          </Card>
        ))}
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Printable Documents
      </Typography>
      <Typography variant="body2" sx={{ mb: 3 }}>
        Select one or more documents to print. Use the print icons for single
        documents or check multiple and print them together.
      </Typography>

      {selectedDocs.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PrintIcon />}
            onClick={handlePrintSelected}
          >
            Print Selected ({selectedDocs.length})
          </Button>
        </Box>
      )}

      {renderList("Mandatory documents before berth", mandatoryDocuments)}
      {renderList("Other printable documents", otherDocuments)}
    </Box>
  );
}
